"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import { trackEvent } from "@/lib/gtag";

interface Accent {
  label: string;
  flag: string;
  lang: string;
  /** Speech API 用：嘗試匹配的語音名稱 */
  voiceMatch: string[];
  /** 用 Google Translate TTS 取代 Speech API */
  useGoogleTTS?: boolean;
  /** Google TTS 語言代碼 */
  googleLang?: string;
}

const ACCENTS: Accent[] = [
  { label: "美式", flag: "🇺🇸", lang: "en-US", voiceMatch: ["Samantha", "Alex", "Google US", "Microsoft Mark", "en-US"] },
  { label: "英式", flag: "🇬🇧", lang: "en-GB", voiceMatch: ["Daniel", "Google UK", "Microsoft Hazel", "en-GB"] },
  { label: "澳洲", flag: "🇦🇺", lang: "en-AU", voiceMatch: ["Karen", "Google AU", "en-AU"] },
  { label: "印度", flag: "🇮🇳", lang: "en-IN", voiceMatch: ["Rishi", "Google IN", "en-IN"] },
  { label: "中國", flag: "🇨🇳", lang: "zh-CN", voiceMatch: [], useGoogleTTS: true, googleLang: "zh-CN" },
  { label: "台灣", flag: "🇹🇼", lang: "zh-TW", voiceMatch: [], useGoogleTTS: true, googleLang: "zh-TW" },
  { label: "日本", flag: "🇯🇵", lang: "ja-JP", voiceMatch: [], useGoogleTTS: true, googleLang: "ja" },
];

function getGoogleTTSUrl(word: string, lang: string): string {
  return `https://translate.google.com/translate_tts?ie=UTF-8&client=tw-ob&tl=${lang}&q=${encodeURIComponent(word)}`;
}

export default function MultiAccentPlayer({ word }: { word: string }) {
  const [playing, setPlaying] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  const stop = useCallback(() => {
    if (typeof window !== "undefined" && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    setPlaying(null);
  }, []);

  const playGoogleTTS = useCallback(
    (accent: Accent) => {
      stop();

      const audio = new Audio(getGoogleTTSUrl(word, accent.googleLang || accent.lang));
      audioRef.current = audio;
      setPlaying(accent.lang);

      audio.onended = () => setPlaying(null);
      audio.onerror = () => {
        setPlaying(null);
        // fallback 到 Speech API
        if (window.speechSynthesis) {
          const utterance = new SpeechSynthesisUtterance(word);
          utterance.lang = accent.lang;
          utterance.rate = 0.85;
          utterance.onend = () => setPlaying(null);
          utterance.onerror = () => setPlaying(null);
          setPlaying(accent.lang);
          window.speechSynthesis.speak(utterance);
        }
      };
      audio.play().catch(() => setPlaying(null));
    },
    [word, stop]
  );

  const playSpeechAPI = useCallback(
    (accent: Accent) => {
      if (typeof window === "undefined" || !window.speechSynthesis) return;
      stop();

      const utterance = new SpeechSynthesisUtterance(word);
      utterance.lang = accent.lang;
      utterance.rate = 0.85;

      const voices = window.speechSynthesis.getVoices();
      const bestVoice =
        voices.find((v) => accent.voiceMatch.some((m) => v.name.includes(m))) ||
        voices.find((v) => v.lang === accent.lang) ||
        voices.find((v) => v.lang.startsWith(accent.lang.split("-")[0]));

      if (bestVoice) utterance.voice = bestVoice;

      utterance.onend = () => setPlaying(null);
      utterance.onerror = () => setPlaying(null);

      setPlaying(accent.lang);
      window.speechSynthesis.speak(utterance);
    },
    [word, stop]
  );

  const handlePlay = useCallback(
    (accent: Accent) => {
      // 正在播放同一個 → 停止
      if (playing === accent.lang) {
        stop();
        return;
      }

      trackEvent("accent_play", {
        word,
        accent: accent.label,
        accent_lang: accent.lang,
      });

      if (accent.useGoogleTTS) {
        playGoogleTTS(accent);
      } else {
        playSpeechAPI(accent);
      }
    },
    [word, playing, stop, playGoogleTTS, playSpeechAPI]
  );

  if (!mounted) return null;

  return (
    <div className="flex flex-wrap gap-1.5">
      {ACCENTS.map((accent) => (
        <button
          key={accent.lang}
          onClick={() => handlePlay(accent)}
          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition ${
            playing === accent.lang
              ? "bg-amber-700 text-white"
              : "bg-amber-50 text-amber-700 hover:bg-amber-100"
          }`}
        >
          <span>{accent.flag}</span>
          <span>{accent.label}</span>
        </button>
      ))}
    </div>
  );
}
